/**
 * BUFFING POWDER DETAIL MODAL - MATCHES PLATEDETAILMODAL STYLE
 * 
 * Features:
 * - Responsive two-column layout (desktop) / stacked (mobile)
 * - Photo caption & credit synced with carousel
 * - Collapsible sections for Tags, Notes, Toggles, Inventory
 * - Photo editing mode (upload, delete, set default, edit captions)
 * - Usage tracker (always visible)
 * - Destash modal with tracking
 * - Delete confirmation
 * - Cyan color scheme
 */

import React, { useState, useEffect } from 'react';
import { X, Heart, Info, Repeat, Save, Plus, ChevronDown, ChevronRight, ChevronLeft, Eye, Store, Sparkles, ExternalLink, Star, Trash2, RefreshCw, GripVertical, Pencil } from 'lucide-react';
import { SortablePhotoGrid } from '../common/SortablePhotoGrid';
import PhotoEditModal, { buildPhotoSummary } from '../common/PhotoEditModal';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { getCurrentUser } from '../../firebase/auth';
import { buildDestashFields, clearDestashFields } from '../../services/destashDenormalization';
import { uploadPhoto, deletePhoto } from '../../services/photoUploadService';
import StorageUpgradeModal from '../StorageUpgradeModal';
import ImageCarousel from '../ImageCarousel';
import ConditionSelector from '../common/ConditionSelector';
import PrivateLockBadge from '../common/PrivateLockBadge';
import { useSellerCurrency } from '../../hooks/useSellerCurrency';
import { usePinnedCurrencies } from '../../hooks/usePinnedCurrencies';
import { formatPrice, getCurrencyInfo } from '../../constants/currencies';
import CurrencyPicker from '../common/CurrencyPicker';
import type { CollectionItem, Photo } from '../../types';
import { AppIcons } from '../../constants/icons';
import PerPhotoReAttest from '../common/PerPhotoReAttest';
import ReleaseInfoDisplay from '../common/ReleaseInfoDisplay';
import ReleaseInfoSection, {
  type ReleaseInfoValue,
  releaseInfoSaveShape,
  seedReleaseInfoFromItem,
} from '../common/ReleaseInfoSection';
import { CategoryColors, PriorityColors, type PriorityKey } from '../../constants/categoryColors';
import { ACCEPTED_IMAGE_TYPES } from '../../utils/imageUtils';
import { normalizeTypography } from '../../utils/normalizeTypography';

interface BuffingPowderDetailModalProps {
  powder: CollectionItem;
  onClose: () => void;
  onUpdate?: (powderId: string, updates: Partial<CollectionItem>) => void;
  onDelete?: (powderId: string) => void;
  onRestore?: (powderId: string) => void;
  onPermanentDelete?: (powderId: string) => void;
  isInTrash?: boolean;
  onNext?: () => void;
  onPrev?: () => void;
  hasNext?: boolean;
  hasPrev?: boolean;
  isPublicView?: boolean;
}

export default function BuffingPowderDetailModal({ powder, onClose, onUpdate, onDelete, onRestore, onPermanentDelete, isInTrash = false, onNext, onPrev, hasNext, hasPrev, isPublicView = false }: BuffingPowderDetailModalProps) {
  // Toggle states
  const [onWishlist, setOnWishlist] = useState(powder.onWishlist || false);
  const [isoPriority, setIsoPriority] = useState<'iso' | 'diso' | 'unicorn'>(powder.isoPriority || 'iso');
  const [needsInfo, setNeedsInfo] = useState(powder.needsInfo || false);
  const [isFavorite, setIsFavorite] = useState(powder.isFavorite || false);
  const [isRareLE, setIsRareLE] = useState(powder.isRareLE || false);
  // Private item — hides from all public surfaces. Marketplace is
  // intentionally unaffected; if isForDestash is also on, buyers still
  // see this on Marketplace and we warn the user about it inline.
  const [isPrivate, setIsPrivate] = useState(powder.isPrivate || false);
  const [swatched, setSwatched] = useState(powder.swatched || false);

  // Seller's chosen currency from profile. Drives the symbol shown in the
  // price inputs so a EUR seller doesn't see "$" next to their price. The
  // save side (buildDestashFields) reads the same profile field, so what
  // the seller sees here matches what buyers see on Marketplace.
  const currency = useSellerCurrency();
  const { pinnedCurrencies, setPinnedCurrencies } = usePinnedCurrencies();
  // Per-item purchase currency (owner record-keeping). Seed from stored
  // value on the item; empty string = "use profile currency by default".
  const [purchaseCurrency, setPurchaseCurrency] = useState<string>(powder.priceCurrency || '');
  const purchaseSymbol = purchaseCurrency
    ? getCurrencyInfo(purchaseCurrency).symbol
    : currency.symbol;
  const purchaseSymbolPad =
    purchaseSymbol.length <= 1 ? 'pl-7' : purchaseSymbol.length <= 2 ? 'pl-10' : 'pl-12';
  const symbolPad =
    currency.symbol.length <= 1 ? 'pl-7' : currency.symbol.length <= 2 ? 'pl-10' : 'pl-12';

  // Marketplace / Destash listing state
  const [isForDestash, setIsForDestash] = useState(powder.isForDestash || false);
  const [destashCondition, setDestashCondition] = useState(powder.destashCondition || '');
  const [destashPrice, setDestashPrice] = useState(powder.destashPrice?.toString() || '');
  const [isOpenToSwaps, setIsOpenToSwaps] = useState(powder.isOpenToSwaps || false);
  const [destashNotes, setDestashNotes] = useState(powder.destashNotes || '');
  const [destashQuantityAvailable, setDestashQuantityAvailable] = useState(powder.destashQuantityAvailable || powder.quantity || 1);
  const [destashSaved, setDestashSaved] = useState(false);
  const [showListedSuccess, setShowListedSuccess] = useState(false);
  const [wasAlreadyListed] = useState(powder.isForDestash || false);

  // Archive/Destash completion modals (for when item actually leaves collection)
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [archiveMethod, setArchiveMethod] = useState<string>('sold');
  const [archiveSalePrice, setArchiveSalePrice] = useState<string>('');
  const [archiveNotes, setArchiveNotes] = useState<string>('');

  // Usage tracking
  const [timesUsed, setTimesUsed] = useState(powder.timesUsed || 0);
  const [lastUsedDate, setLastUsedDate] = useState(powder.lastUsedDate);
  const [usageSaved, setUsageSaved] = useState(false);
  const [isEditingUsage, setIsEditingUsage] = useState(false);

  // Edit mode
  const [isEditing, setIsEditing] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [_editSaved, setEditSaved] = useState(false);

  // Editable fields
  const [brand, setBrand] = useState(powder.brand || '');
  const [name, setName] = useState(powder.name || '');
  const [powderType, setPowderType] = useState(powder.powderType || '');
  const [effect, setEffect] = useState(powder.effect || '');
  const [color, setColor] = useState(powder.color || '');
  const [size, setSize] = useState(powder.size || '');
  const [storage_, setStorage_] = useState(powder.storage || '');
  const [barcode, setBarcode] = useState(powder.barcode || '');
  const [price, setPrice] = useState(powder.price?.toString() || '');
  // Purchase date lives on `powder.purchaseDate` as a Date | Firestore
  // Timestamp | ISO string depending on origin. Normalize to a YYYY-MM-DD
  // string for the <input type="date"> control — empty string means unset.
  const [purchaseDate, setPurchaseDate] = useState(() => {
    const v: any = powder.purchaseDate;
    if (!v) return '';
    try {
      const d =
        v instanceof Date
          ? v
          : typeof v?.toDate === 'function'
            ? v.toDate()
            : new Date(v);
      if (isNaN(d.getTime())) return '';
      return d.toISOString().slice(0, 10);
    } catch {
      return '';
    }
  });
  const [quantity, setQuantity] = useState(powder.quantity?.toString() || '1');

  /** Consolidated release-info state — matches DecalDetailModal /
   *  PlateDetailModal pattern. */
  const [releaseInfo, setReleaseInfo] = useState<ReleaseInfoValue>(
    seedReleaseInfoFromItem(powder)
  );

  // Notes
  const [publicNotes, setPublicNotes] = useState(powder.publicNotes || '');
  const [privateNotes, setPrivateNotes] = useState(powder.notes || '');
  const [notesSaved, setNotesSaved] = useState(false);
  const [toggleSaved, setToggleSaved] = useState(false);

  // Tags
  const [tags, setTags] = useState<string[]>(powder.tags || []);
  const [newTag, setNewTag] = useState('');
  const [tagsSaved, setTagsSaved] = useState(false);

  // Photo management
  const getInitialPhotos = (): Photo[] => {
    if (powder.photos && powder.photos.length > 0) {
      return powder.photos;
    }
    if (powder.photoUrl) {
      return [{ url: powder.photoUrl, isDefault: true }];
    }
    return [];
  };
  
  const [photos, setPhotos] = useState<Photo[]>(getInitialPhotos());
  const [editingPhotoIdx, setEditingPhotoIdx] = useState<number | null>(null);
  const [isEditingPhotos, setIsEditingPhotos] = useState(false);
  const [photosSaved, setPhotosSaved] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [showStorageUpgradeModal, setShowStorageUpgradeModal] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // Collapsible sections
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Public view - Add to ISO/PublicWishlist
  const [showIsoMenu, setShowIsoMenu] = useState(false);
  const [addedToIso, setAddedToIso] = useState(false);
  const [isoLevel, setIsoLevel] = useState<'iso' | 'diso' | 'unicorn' | null>(null);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  // Sync state when powder prop changes
  useEffect(() => {
    setNeedsInfo(powder.needsInfo || false);
    setOnWishlist(powder.onWishlist || false);
    setIsoPriority(powder.isoPriority || 'iso');
    setIsFavorite(powder.isFavorite || false);
    setIsRareLE(powder.isRareLE || false);
    setIsPrivate(powder.isPrivate || false);
    setSwatched(powder.swatched || false);
    setTimesUsed(powder.timesUsed || 0);
    setLastUsedDate(powder.lastUsedDate);
    setTags(powder.tags || []);

    // Sync destash listing fields
    setIsForDestash(powder.isForDestash || false);
    setDestashCondition(powder.destashCondition || '');
    setDestashPrice(powder.destashPrice?.toString() || '');
    setIsOpenToSwaps(powder.isOpenToSwaps || false);
    setDestashNotes(powder.destashNotes || '');
    setDestashQuantityAvailable(powder.destashQuantityAvailable || powder.quantity || 1);
  }, [powder]);

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Close ISO menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (showIsoMenu) setShowIsoMenu(false);
    };
    if (showIsoMenu) {
      setTimeout(() => document.addEventListener('click', handleClickOutside), 100);
    }
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showIsoMenu]);

  // Handle adding to own wishlist when viewing someone else's item
  const handleAddToMyWishlist = async (priority: 'iso' | 'diso' | 'unicorn') => {
    const currentUser = getCurrentUser();
    if (!currentUser) return;
    try {
      const itemsRef = collection(db, 'users', currentUser.uid, 'items');
      
      // Copy first photo WITH source tracking for DMCA
      const trackedPhotos = powder.photos?.slice(0, 1).map(photo => ({
        ...photo,
        // DMCA Source Tracking
        uploadedBy: currentUser.uid,
        uploadedAt: new Date().toISOString(),
        source: 'web' as const,
        sourceUrl: photo.url,
        sourceUserId: powder.userId || undefined,
        dmcaStatus: 'active' as const,
        ...(photo.creditName && { creditName: photo.creditName }),
        ...(photo.creditUrl && { creditUrl: photo.creditUrl }),
      })) || [];

      // Normalize smart-quote / typographic-dash variants down to plain
      // ASCII when copying so the copied item lands in Firestore in the
      // canonical form even if the source item predates normalization.
      const normalizedBrand = normalizeTypography((powder.brand ?? '').trim());
      const normalizedName = normalizeTypography((powder.name ?? '').trim());
      await addDoc(itemsRef, {
        brand: normalizedBrand,
        brandLower: normalizedBrand.toLowerCase(),
        name: normalizedName,
        nameLower: normalizedName.toLowerCase(),
        itemType: 'buffingPowder',
        onWishlist: true,
        isoPriority: priority,
        quantity: 0,
        photos: trackedPhotos,
        tags: powder.tags || [],
        publicNotes: `Spotted in another collector's collection`,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setAddedToIso(true);
      setIsoLevel(priority);
      setShowIsoMenu(false);
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      alert('Failed to add to your wishlist. Please try again.');
    }
  };

  // Track unsaved changes
  useEffect(() => {
    if (!isEditing) {
      setHasUnsavedChanges(false);
      return;
    }
    const hasChanges = 
      brand !== (powder.brand || '') ||
      name !== (powder.name || '') ||
      powderType !== (powder.powderType || '') ||
      effect !== (powder.effect || '') ||
      color !== (powder.color || '') ||
      size !== (powder.size || '') ||
      storage_ !== (powder.storage || '') ||
      barcode !== (powder.barcode || '') ||
      price !== (powder.price?.toString() || '') ||
      purchaseCurrency !== (powder.priceCurrency || '') ||
      purchaseDate !== (() => {
        const v: any = powder.purchaseDate;
        if (!v) return '';
        try {
          const d = v instanceof Date ? v : typeof v?.toDate === 'function' ? v.toDate() : new Date(v);
          return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
        } catch { return ''; }
      })() ||
      quantity !== (powder.quantity?.toString() || '1') ||
      JSON.stringify(releaseInfo) !== JSON.stringify(seedReleaseInfoFromItem(powder)) ||
      isPrivate !== (powder.isPrivate || false);
    setHasUnsavedChanges(hasChanges);
  }, [isEditing, brand, name, powderType, effect, color, size, storage_, barcode, price, purchaseCurrency, purchaseDate, quantity, releaseInfo, isPrivate, powder]);

  // Toggle handler (for simple toggles only)
  const handleToggle = async (field: string, value: boolean) => {
    switch (field) {
      case 'onWishlist': 
        setOnWishlist(value);
        if (value && !powder.isoPriority) {
          setIsoPriority('iso');
        }
        break;
      case 'needsInfo': setNeedsInfo(value); break;
      case 'isFavorite': setIsFavorite(value); break;
      case 'isRareLE': setIsRareLE(value); break;
      case 'swatched': setSwatched(value); break;
      case 'isPrivate': setIsPrivate(value); break;
    }

    if (onUpdate) {
      const updates: any = { [field]: value };
      if (field === 'onWishlist' && value) {
        updates.isoPriority = powder.isoPriority || 'iso';
      }
      await onUpdate(powder.id, updates);
      setToggleSaved(true);
      setTimeout(() => setToggleSaved(false), 1500);
    }
  };

  const handleIsoPriorityChange = async (priority: 'iso' | 'diso' | 'unicorn') => {
    setIsoPriority(priority);
    if (onUpdate) {
      await onUpdate(powder.id, { isoPriority: priority });
      setToggleSaved(true);
      setTimeout(() => setToggleSaved(false), 1500);
    }
  };

  // Destash listing handler - saves all destash fields together
  const handleSaveDestashListing = async () => {
    if (!onUpdate) return;
    
    if (isForDestash) {
      // Validate required fields
      if (!destashCondition) {
        alert('Please select a condition to list on the marketplace');
        return;
      }
      if (!destashPrice) {
        alert('Please enter a price to list on the marketplace');
        return;
      }
      
      const currentUser = getCurrentUser();
      const denorm = currentUser ? await buildDestashFields(currentUser.uid) : clearDestashFields();
      await onUpdate(powder.id, {
        isForDestash: true,
        destashCondition: destashCondition as 'New' | 'Used - Light Use' | 'Swatched Only' | 'Used - Moderate',
        destashPrice: parseFloat(destashPrice),
        destashQuantityAvailable,
        isOpenToSwaps,
        destashNotes: destashNotes || undefined,
        destashStatus: 'available',
        destashDate: (powder.destashDate || new Date()) as Date,
        ...denorm,
      });
      
      // Show success banner (persists until modal closes)
      setShowListedSuccess(true);
      // Success banner now persists until modal closes
    } else {
      // Turning off destash - clear marketplace fields
      await onUpdate(powder.id, {
        isForDestash: false,
        destashCondition: undefined,
        destashPrice: undefined,
        destashQuantityAvailable: undefined,
        isOpenToSwaps: false,
        destashNotes: undefined,
        destashStatus: undefined,
        ...clearDestashFields(),
      });
    }
    
    setDestashSaved(true);
    setTimeout(() => setDestashSaved(false), 2000);
  };

  // Toggle destash listing on/off
  const handleToggleDestash = (enabled: boolean) => {
    setIsForDestash(enabled);
    if (enabled) {
      // Set default quantity to total quantity when enabling
      setDestashQuantityAvailable(parseInt(quantity) || 1);
    } else {
      // Clear fields when turning off
      setDestashCondition('');
      setDestashPrice('');
      setIsOpenToSwaps(false);
      setDestashNotes('');
      setDestashQuantityAvailable(parseInt(quantity) || 1);
      // Auto-save the removal
      if (onUpdate) {
        onUpdate(powder.id, {
          isForDestash: false,
          destashCondition: undefined,
          destashPrice: undefined,
          destashQuantityAvailable: undefined,
          isOpenToSwaps: false,
          destashNotes: undefined,
          destashStatus: undefined,
          ...clearDestashFields(),
        });
        setDestashSaved(true);
        setTimeout(() => setDestashSaved(false), 2000);
      }
    }
  };

  // Usage tracking
  const handleLogUsage = async () => {
    const newTimesUsed = timesUsed + 1;
    const newLastUsedDate = new Date();
    setTimesUsed(newTimesUsed);
    setLastUsedDate(newLastUsedDate);
    if (onUpdate) {
      await onUpdate(powder.id, { timesUsed: newTimesUsed, lastUsedDate: newLastUsedDate });
      setUsageSaved(true);
      setTimeout(() => setUsageSaved(false), 1500);
    }
  };

  const handleSaveUsageEdit = async () => {
    if (onUpdate) {
      // Keep the counter and the date coherent — a saved Last Used date
      // with a count of 0 renders as "0 times used" and looks broken. If
      // the user set a date but left the count at 0, promote it to 1.
      const coercedTimesUsed =
        lastUsedDate && (timesUsed == null || timesUsed <= 0) ? 1 : timesUsed;
      const updates: any = { timesUsed: coercedTimesUsed };
      if (lastUsedDate) {
        updates.lastUsedDate = lastUsedDate;
      }
      await onUpdate(powder.id, updates);
      setUsageSaved(true);
      setIsEditingUsage(false);
      setTimeout(() => setUsageSaved(false), 1500);
    }
  };

  // Notes handler
  const handleSaveNotes = async () => {
    if (onUpdate) {
      await onUpdate(powder.id, {
        publicNotes: publicNotes.trim(),
        notes: privateNotes.trim()
      });
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 2000);
    }
  };

  // Tags handlers
  const handleAddTag = async () => {
    const trimmedTag = newTag.trim();
    if (!trimmedTag || tags.includes(trimmedTag)) {
      setNewTag('');
      return;
    }
    const updatedTags = [...tags, trimmedTag];
    setTags(updatedTags);
    setNewTag('');
    
    if (onUpdate) {
      await onUpdate(powder.id, { tags: updatedTags });
      setTagsSaved(true);
      setTimeout(() => setTagsSaved(false), 1500);
    }
  };

  const handleRemoveTag = async (tagToRemove: string) => {
    const updatedTags = tags.filter(t => t !== tagToRemove);
    setTags(updatedTags);
    
    if (onUpdate) {
      await onUpdate(powder.id, { tags: updatedTags });
      setTagsSaved(true);
      setTimeout(() => setTagsSaved(false), 1500);
    }
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  // Edit handlers
  const handleEdit = () => setIsEditing(true);

  const handleCancelEdit = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to cancel?');
      if (!confirmed) return;
    }
    setBrand(powder.brand || '');
    setName(powder.name || '');
    setPowderType(powder.powderType || '');
    setEffect(powder.effect || '');
    setColor(powder.color || '');
    setSize(powder.size || '');
    setStorage_(powder.storage || '');
    setBarcode(powder.barcode || '');
    setPrice(powder.price?.toString() || '');
    setPurchaseCurrency(powder.priceCurrency || '');
    setPurchaseDate(() => {
      const v: any = powder.purchaseDate;
      if (!v) return '';
      try {
        const d = v instanceof Date ? v : typeof v?.toDate === 'function' ? v.toDate() : new Date(v);
        return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
      } catch { return ''; }
    });
    setQuantity(powder.quantity?.toString() || '1');
    setReleaseInfo(seedReleaseInfoFromItem(powder));
    setIsPrivate(powder.isPrivate || false);
    setIsEditing(false);
    setHasUnsavedChanges(false);
  };

  const handleSaveEdit = async () => {
    if (onUpdate) {
      // Normalize smart-quote / typographic-dash variants down to plain
      // ASCII on rename so an iOS user renaming a powder writes the same
      // string as an Android user. Prevents typography-only duplicates.
      const normalizedBrand = normalizeTypography(brand.trim());
      const normalizedName = normalizeTypography(name.trim());
      await onUpdate(powder.id, {
        brand: normalizedBrand,
        name: normalizedName,
        powderType: powderType || undefined,
        effect: effect.trim() || undefined,
        color: color.trim() || undefined,
        size: size.trim() || undefined,
        storage: storage_.trim() || undefined,
        barcode: barcode.trim() || undefined,
        price: price ? parseFloat(price) : undefined,
        priceCurrency: price && purchaseCurrency ? purchaseCurrency : (powder.priceCurrency !== undefined ? null : undefined) as any,
        purchaseDate: purchaseDate ? (new Date(purchaseDate) as any) : (powder.purchaseDate !== undefined ? (null as any) : undefined),
        quantity: parseInt(quantity) || 1,
        // Release info via the shared shape helper — writes only the
        // fields relevant to the current release type. eventSource is
        // explicitly nulled here to complete the Jan 2026 rename
        // migration for anything the edit form touches.
        ...releaseInfoSaveShape(releaseInfo),
        eventSource: null as any,
        // Privacy flag — write unconditionally so toggling OFF also persists.
        isPrivate,
      });
      setIsEditing(false);
      setHasUnsavedChanges(false);
      setEditSaved(true);
      setTimeout(() => setEditSaved(false), 2000);
    }
  };

  // Archive handler (item actually left collection - sold, traded, etc.)
  const handleArchive = async () => {
    if (onUpdate) {
      await onUpdate(powder.id, {
        isDestashed: true,
        destashDate: new Date(),
        destashMethod: archiveMethod as any,
        destashSalePrice: archiveSalePrice ? parseFloat(archiveSalePrice) : undefined,
        destashNotes: archiveNotes.trim() || undefined,
        isForDestash: false,
        destashCondition: undefined,
        destashPrice: undefined,
        isOpenToSwaps: false,
        destashStatus: undefined,
        ...clearDestashFields(),
      });
      setShowArchiveModal(false);
      onClose();
    }
  };

  // Un-archive handler (bring back to collection)
  const handleUnArchive = async () => {
    if (onUpdate) {
      await onUpdate(powder.id, {
        isDestashed: false,
        destashDate: undefined,
        destashMethod: undefined,
        destashSalePrice: undefined,
      });
    }
  };

  // Delete handler (soft delete - moves to trash)
  const handleDelete = async () => {
    if (onDelete) {
      await onDelete(powder.id);
      onClose();
    }
  };

  // Restore handler (restore from trash)
  const handleRestore = async () => {
    if (onRestore) {
      await onRestore(powder.id);
      onClose();
    }
  };

  // Permanent delete handler (actually removes from database)
  const [showPermanentDeleteConfirm, setShowPermanentDeleteConfirm] = useState(false);
  const handlePermanentDelete = async () => {
    if (onPermanentDelete) {
      await onPermanentDelete(powder.id);
      setShowPermanentDeleteConfirm(false);
      onClose();
    }
  };

  // Photo handlers
  const setDefaultPhoto = async (index: number) => {
    const updatedPhotos = photos.map((photo, i) => ({
      ...photo,
      isDefault: i === index
    }));
    setPhotos(updatedPhotos);
    if (onUpdate) {
      await onUpdate(powder.id, { photos: updatedPhotos });
      setPhotosSaved(true);
      setTimeout(() => setPhotosSaved(false), 1500);
    }
  };

  /** Build the persist-shape for a Photo — trim optional strings, preserve
   *  photoRightsAttested (a one-shot rights toggle that must NOT be dropped
   *  when other photo actions save). */
  const cleanPhotoForSave = (photo: Photo): Photo => {
    const cleaned: Photo = { url: photo.url, isDefault: photo.isDefault };
    if (photo.caption?.trim()) cleaned.caption = photo.caption.trim();
    if (photo.creditName?.trim()) cleaned.creditName = photo.creditName.trim();
    if (photo.creditUrl?.trim()) cleaned.creditUrl = photo.creditUrl.trim();
    if (photo.photoRightsAttested === true) cleaned.photoRightsAttested = true;
    return cleaned;
  };

  /** Mark a legacy photo as photo-rights-attested (never unset). Persists
   *  immediately — the PerPhotoReAttest checkbox is a one-shot attestation
   *  that should stick even if the user closes the modal without another
   *  action. */
  const attestPhoto = async (index: number) => {
    const updatedPhotos = photos.map((photo, i) => i === index ? { ...photo, photoRightsAttested: true } : photo);
    setPhotos(updatedPhotos);
    if (onUpdate) {
      const cleanedPhotos = updatedPhotos.map(cleanPhotoForSave);
      await onUpdate(powder.id, { photos: cleanedPhotos });
    }
  };

  const removePhoto = async (index: number) => {
    const photoToRemove = photos[index];
    const currentUser = getCurrentUser();

    // Delete from Firebase Storage and update usage count
    if (currentUser) {
      await deletePhoto(currentUser.uid, photoToRemove.url);
    }

    let updatedPhotos = photos.filter((_, i) => i !== index);
    if (updatedPhotos.length > 0 && !updatedPhotos.some(p => p.isDefault)) {
      updatedPhotos[0].isDefault = true;
    }
    setPhotos(updatedPhotos);
    if (onUpdate) {
      const cleanedPhotos = updatedPhotos.map(cleanPhotoForSave);
      await onUpdate(powder.id, { photos: cleanedPhotos });
      setPhotosSaved(true);
      setTimeout(() => setPhotosSaved(false), 1500);
    }
  };

  const handleSavePhotos = async () => {
    if (!onUpdate) return;
    const cleanedPhotos = photos.map(cleanPhotoForSave);
    await onUpdate(powder.id, { photos: cleanedPhotos });
    setPhotosSaved(true);
    setIsEditingPhotos(false);
    setTimeout(() => setPhotosSaved(false), 1500);
  };

  const handleCancelPhotoEdit = () => {
    setPhotos(powder.photos || []);
    setIsEditingPhotos(false);
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const currentUser = getCurrentUser();
    if (!currentUser) { alert('Please log in to upload photos'); return; }
    setIsUploadingPhoto(true);
    try {
      const newPhotos: Photo[] = [];
      for (const file of Array.from(files)) {
        const nameWithoutExt = file.name.replace(/\.(jpg|jpeg|png|gif|webp)$/i, '');
        const parts = nameWithoutExt.split(' - ');
        const caption = parts.length >= 2 ? parts.slice(1).join(' - ').trim() : '';
        
        const result = await uploadPhoto(
          currentUser.uid,
          file,
          'powders',
          { caption: caption || undefined, isDefault: photos.length === 0 && newPhotos.length === 0 }
        );
        
        if (result.showUpgradeModal) {
          setShowStorageUpgradeModal(true);
          break;
        }
        
        if (result.success && result.photo) {
          newPhotos.push(result.photo);
        }
      }
      
      if (newPhotos.length > 0) {
        setPhotos(current => [...current, ...newPhotos]);
        if (onUpdate) {
          const allPhotos = [...photos, ...newPhotos].map(cleanPhotoForSave);
          await onUpdate(powder.id, { photos: allPhotos });
          setPhotosSaved(true);
          setTimeout(() => setPhotosSaved(false), 1500);
        }
      }
    } catch (error) {
      console.error('Error uploading photos:', error);
      alert('Failed to upload photos. Please try again.');
    } finally {
      setIsUploadingPhoto(false);
      e.target.value = '';
    }
  };

  // Close handler
  const handleClose = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to close?');
      if (!confirmed) return;
    }
    onClose();
  };

  // Navigation handlers with unsaved changes warning
  const handlePrev = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to go to the previous item?');
      if (!confirmed) return;
    }
    onPrev?.();
  };

  const handleNext = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to go to the next item?');
      if (!confirmed) return;
    }
    onNext?.();
  };

  // Get images for carousel
  const getImages = (): string[] => {
    if (photos && photos.length > 0) {
      // Respect array order (the drag-to-reorder feature persists the
      // user's chosen order). isDefault is now purely for cover-tile
      // selection on cards, not for detail-page display order.
      return photos.map(p => p.url);
    }
    if (powder.photoUrl) return [powder.photoUrl];
    return [];
  };

  const images = getImages();

  // Get current photo data for caption/credit display
  const getCurrentPhotoData = (): Photo | null => {
    if (photos && photos.length > 0) {
      // Respect array order (must match getImages).
      return photos[currentPhotoIndex] || null;
    }
    return null;
  };

  const currentPhoto = getCurrentPhotoData();

  // Get active toggle icons for collapsed summary
  const getActiveToggleIcons = () => {
    const icons: React.ReactNode[] = [];
    if (isFavorite) icons.push(<AppIcons.Favorite key="fav" className="w-4 h-4 text-current" aria-label="Favorite" />);
    if (onWishlist) icons.push(<AppIcons.Wishlist key="wish" className="w-4 h-4 text-current" aria-label="PublicWishlist" />);
    if (isRareLE) icons.push(<AppIcons.RareLE key="rare" className="w-4 h-4 text-current" aria-label="Rare/Limited Edition" />);
    if (needsInfo) icons.push(<AppIcons.NeedsInfo key="info" className="w-4 h-4 text-current" aria-label="Needs Info" />);
    return icons;
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 md:p-4 z-50">
      {/* Floating Previous Button */}
      {(onPrev || onNext) && (
        <button
          type="button"
          onClick={handlePrev}
          disabled={!hasPrev}
          className="fixed left-2 md:left-4 top-1/2 -translate-y-1/2 z-[60] btn-nav-light disabled:opacity-30 disabled:cursor-not-allowed"
          title="Previous item"
        >
          <ChevronLeft className="icon-base md:w-8 md:h-8 text-gray-600" />
        </button>
      )}

      {/* Floating Next Button */}
      {(onPrev || onNext) && (
        <button
          type="button"
          onClick={handleNext}
          disabled={!hasNext}
          className="fixed right-2 md:right-4 top-1/2 -translate-y-1/2 z-[60] btn-nav-light disabled:opacity-30 disabled:cursor-not-allowed"
          title="Next item"
        >
          <ChevronRight className="icon-base md:w-8 md:h-8 text-gray-600" />
        </button>
      )}

      <div className="card-light w-full max-w-5xl max-h-[95vh] overflow-y-auto overscroll-contain">
        {/* Header */}
        <div className="modal-header-light">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span className={`px-2 py-1 ${CategoryColors.powder.badge} rounded-full text-xs font-bold flex-shrink-0`}>Powder</span>
            <h2 className="text-xl md:text-2xl font-bold text-light-primary truncate font-heading">
              {isEditing ? `Edit: ${name}` : powder.name}
            </h2>
            {powder.isPrivate && <PrivateLockBadge variant="chip" />}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {isPublicView ? (
              <>
                {addedToIso ? (
                  <span className="px-3 md:px-4 py-2 bg-green-100 text-green-700 rounded-lg font-medium text-sm md:text-base flex items-center gap-1">
                    <Sparkles className="icon-sm" />
                    Added as {PriorityColors[isoLevel as PriorityKey]?.label || PriorityColors.iso.label}!
                  </span>
                ) : (
                  <div className="relative">
                    <button 
                      type="button"
                      onClick={() => setShowIsoMenu(!showIsoMenu)}
                      className="px-3 md:px-4 py-2 bg-pink-500 text-white rounded-lg font-medium hover:bg-pink-600 transition-default text-sm md:text-base flex items-center gap-1"
                    >
                      <Eye className="icon-sm" />
                      Add to ISO
                      <ChevronDown className="icon-sm" />
                    </button>
                    {showIsoMenu && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-300 py-2 z-20">
                        <button type="button" onClick={() => handleAddToMyWishlist('iso')} className="w-full px-4 py-2 text-left hover:bg-pink-50 text-gray-600 flex items-center gap-2">
                          💎 ISO <span className="text-xs text-gray-500">(In Search Of)</span>
                        </button>
                        <button type="button" onClick={() => handleAddToMyWishlist('diso')} className="w-full px-4 py-2 text-left hover:bg-rose-50 text-gray-600 flex items-center gap-2">
                          🔥 DISO <span className="text-xs text-gray-500">(Desperately)</span>
                        </button>
                        <button type="button" onClick={() => handleAddToMyWishlist('unicorn')} className="w-full px-4 py-2 text-left hover:bg-slate-700 text-gray-600 flex items-center gap-2">
                          🦄 Unicorn <span className="text-xs text-gray-500">(Grail item!)</span>
                        </button>
                      </div>
                    )}
                  </div>
                )}
                <button type="button" onClick={handleClose} className="p-2 hover:bg-gray-50 rounded-full transition-default">
                  <X className="icon-md md:w-6 md:h-6" />
                </button>
              </>
            ) : !isEditing ? (
              <>
                <button
                  type="button"
                  onClick={handleEdit}
                  className={`px-3 md:px-4 py-2 ${CategoryColors.powder.bgActive} text-white rounded-lg font-medium transition-default text-sm md:text-base`}
                >
                  Edit
                </button>
                <button type="button" onClick={handleClose} className="p-2 hover:bg-gray-50 rounded-full transition-default">
                  <X className="icon-md md:w-6 md:h-6" />
                </button>
              </>
            ) : (
              <>
                <button 
                  type="button"
                  onClick={handleSaveEdit}
                  className="px-3 md:px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-default text-sm md:text-base"
                >
                  Save
                </button>
                <button 
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-3 md:px-4 py-2 bg-gray-500 text-white rounded-lg font-medium hover:bg-gray-600 transition-default text-sm md:text-base"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>

        {/* Main Content - Two columns on desktop */}
        <div className="p-4 md:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* LEFT COLUMN: Photos */}
            <div className="space-y-3">
              {images.length > 0 ? (
                <ImageCarousel
                  images={images}
                  alt={`${powder.brand} ${powder.name}`}
                  onSlideChange={setCurrentPhotoIndex}
                />
              ) : (
                <div className="w-full h-64 md:h-80 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-8xl">✨</span>
                </div>
              )}

              {/* Photo Caption & Credit */}
              {currentPhoto && (currentPhoto.caption || currentPhoto.creditName || currentPhoto.dmcaStatus) && (
                <div className={`border rounded-lg p-3 text-sm ${currentPhoto.dmcaStatus === 'hidden' ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}>
                  {currentPhoto.dmcaStatus === 'hidden' && (
                    <p className="text-red-600 font-bold mb-1">⚠️ DMCA Hidden - This photo is not visible to others</p>
                  )}
                  {currentPhoto.caption && (
                    <p className="text-gray-600 italic">{currentPhoto.caption}</p>
                  )}
                  {currentPhoto.creditName && (
                    <p className="text-gray-500 mt-1">
                      📸 {currentPhoto.creditUrl ? (
                        <a href={currentPhoto.creditUrl} target="_blank" rel="noopener noreferrer" className={`${CategoryColors.powder.text} hover:underline`}>
                          {currentPhoto.creditName}
                        </a>
                      ) : currentPhoto.creditName}
                    </p>
                  )}
                </div>
              )}

              {/* Photo Actions - Owner only */}
              {!isPublicView && (
              <div className="flex gap-2">
                {photos.length > 0 && !isEditingPhotos && (
                  <button
                    type="button"
                    onClick={() => setIsEditingPhotos(true)}
                    className="flex-1 px-3 py-2 text-sm border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-200 transition-default"
                  >
                    <AppIcons.EditAction className="w-4 h-4 text-current" aria-label="Edit" />
                    Edit Photos
                  </button>
                )}
                <label className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm rounded-lg cursor-pointer transition-default ${
                  isUploadingPhoto ? 'border-2 border-gray-300 text-gray-500' : `${CategoryColors.powder.light} ${CategoryColors.powder.text} ${CategoryColors.powder.bgHover}`
                }`}>
                  {isUploadingPhoto ? (
                    <span>Uploading...</span>
                  ) : (
                    <>
                      <Plus className="icon-sm" />
                      <span>Add Photos</span>
                    </>
                  )}
                  <input type="file" accept={ACCEPTED_IMAGE_TYPES} multiple onChange={handlePhotoUpload} disabled={isUploadingPhoto} className="hidden" />
                </label>
              </div>
              )}

              {/* Photo Editor (when editing) - Owner only */}
              {!isPublicView && isEditingPhotos && photos.length > 0 && (
                <div className={`border ${CategoryColors.powder.lightBorder} rounded-lg p-3 ${CategoryColors.powder.light} space-y-3`}>
                  <div className="flex items-center justify-between">
                    <span className={`font-medium ${CategoryColors.powder.text} text-sm`}>Editing Photos</span>
                    <div className="flex gap-2">
                      <button type="button" onClick={handleSavePhotos} className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700">Save</button>
                      <button type="button" onClick={handleCancelPhotoEdit} className="px-3 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600">Cancel</button>
                    </div>
                  </div>
                  <SortablePhotoGrid
                    items={photos}
                    onReorder={setPhotos}
                    getId={(p) => p.url}
                    className="space-y-2 max-h-64 overflow-y-auto"
                  >
                    {(photo, idx, dragHandleProps) => (
                      <div className={`flex gap-2 p-2 rounded ${photo.isDefault ? 'bg-yellow-100 border border-yellow-300' : 'bg-white border border-gray-300'}`}>
                        <button
                          type="button"
                          {...dragHandleProps}
                          className="flex-shrink-0 self-center px-1 cursor-grab active:cursor-grabbing touch-none text-gray-400 hover:text-gray-600"
                          title="Drag to reorder"
                          aria-label="Drag to reorder"
                        >
                          <GripVertical className="w-4 h-4" />
                        </button>
                        <img src={photo.url} alt="" className="w-12 h-12 object-cover rounded flex-shrink-0" />
                        <div className="flex-1 space-y-1 min-w-0">
                          <div className="flex items-start gap-2">
                            <div className="flex-1 min-w-0 text-xs text-gray-700">
                              {buildPhotoSummary(photo) || (
                                <span className="text-gray-400 italic">No details yet.</span>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => setEditingPhotoIdx(idx)}
                              className="flex-shrink-0 px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 flex items-center gap-1"
                              title="Edit caption, credit, skin tone, lighting"
                            >
                              <Pencil className="w-3 h-3" />
                              Edit
                            </button>
                          </div>
                          <div className="flex gap-1">
                            {!photo.isDefault && (
                              <button type="button" onClick={() => setDefaultPhoto(idx)} className="px-2 py-0.5 text-xs bg-yellow-200 text-yellow-800 rounded hover:bg-yellow-300">⭐ Default</button>
                            )}
                            <button type="button" onClick={() => removePhoto(idx)} className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"><AppIcons.Delete className="w-3 h-3 text-current" aria-label="Delete" />
                              Remove</button>
                          </div>
                          <PerPhotoReAttest
                            attested={(photo as any).photoRightsAttested === true}
                            onAttest={() => attestPhoto(idx)}
                            itemLabel="powder"
                          />
                        </div>
                      </div>
                    )}
                  </SortablePhotoGrid>
                  <PhotoEditModal
                    open={editingPhotoIdx !== null}
                    photo={(() => {
                      const p = editingPhotoIdx !== null ? photos[editingPhotoIdx] : null;
                      if (!p) return null;
                      return {
                        imageUri: p.url,
                        caption: p.caption,
                        creditName: p.creditName,
                        creditUrl: p.creditUrl,
                        skinTone: p.skinTone,
                        lighting: p.lighting,
                      };
                    })()}
                    onClose={() => setEditingPhotoIdx(null)}
                    onSave={(patch) => {
                      if (editingPhotoIdx === null) return;
                      setPhotos((prev) =>
                        prev.map((p, i) =>
                          i === editingPhotoIdx
                            ? {
                                ...p,
                                caption: patch.caption,
                                creditName: patch.creditName,
                                creditUrl: patch.creditUrl,
                                skinTone: patch.skinTone,
                                lighting: patch.lighting,
                              }
                            : p
                        )
                      );
                    }}
                  />
                </div>
              )}
              {photosSaved && <span className="text-sm text-green-600">✓ Photos saved!</span>}
            </div>

            {/* RIGHT COLUMN: Details - Matching PolishDetailModal style */}
            <div className="space-y-4">
              {/* Brand */}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Brand</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 ${CategoryColors.powder.ring} focus:border-transparent`}
                  />
                ) : (
                  <div className="text-lg font-semibold text-gray-900">{powder.brand}</div>
                )}
              </div>

              {/* Name (edit only) */}
              {isEditing && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 ${CategoryColors.powder.ring} focus:border-transparent`}
                  />
                </div>
              )}

              {/* Powder-specific fields: Type & Effect */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Type</label>
                  {isEditing ? (
                    <select
                      value={powderType}
                      onChange={(e) => setPowderType(e.target.value)}
                      className={`w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 ${CategoryColors.powder.ring} focus:border-transparent`}
                    >
                      <option value="">Select...</option>
                      <option value="Aurora">Aurora</option>
                      <option value="Cat Eye">Cat Eye</option>
                      <option value="Chameleon">Chameleon</option>
                      <option value="Chrome">Chrome</option>
                      <option value="Flakes">Flakes</option>
                      <option value="Glitter">Glitter</option>
                      <option value="Glow">Glow in the Dark</option>
                      <option value="Holographic">Holographic</option>
                      <option value="Magnetic">Magnetic</option>
                      <option value="Mermaid">Mermaid</option>
                      <option value="Mirror">Mirror</option>
                      <option value="Other">Other</option>
                      <option value="Pigment">Pigment</option>
                      <option value="Sugar">Sugar</option>
                      <option value="Thermal">Thermal</option>
                      <option value="Unicorn">Unicorn</option>
                    </select>
                  ) : (
                    <div className="text-gray-900">{powder.powderType || '—'}</div>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Effect</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={effect}
                      onChange={(e) => setEffect(e.target.value)}
                      placeholder="e.g., Blue-to-purple shift"
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 ${CategoryColors.powder.ring} focus:border-transparent`}
                    />
                  ) : (
                    <div className="text-gray-900">{powder.effect || '—'}</div>
                  )}
                </div>
              </div>

              {/* Color & Size */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Color</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      placeholder="e.g., Silver, Gold"
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 ${CategoryColors.powder.ring} focus:border-transparent`}
                    />
                  ) : (
                    <div className="text-gray-900">{powder.color || '—'}</div>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Size/Amount</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={size}
                      onChange={(e) => setSize(e.target.value)}
                      placeholder="e.g., 1g, 2g jar"
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 ${CategoryColors.powder.ring} focus:border-transparent`}
                    />
                  ) : (
                    <div className="text-gray-900">{powder.size || '—'}</div>
                  )}
                </div>
              </div>

              {/* Quantity & Price & Currency Row (inline) */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Quantity</label>
                  {isEditing ? (
                    <input
                      type="number"
                      min="0"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 ${CategoryColors.powder.ring} focus:border-transparent`}
                    />
                  ) : (
                    <div className="text-gray-900">{powder.quantity ?? 1}</div>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Purchase Price</label>
                  {isEditing ? (
                    <>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{purchaseSymbol}</span>
                        <input
                          type="number"
                          step="0.01"
                          value={price}
                          onChange={(e) => setPrice(e.target.value)}
                          className={`w-full ${purchaseSymbolPad} pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 ${CategoryColors.powder.ring} focus:border-transparent`}
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-700 italic">
                        Defaults to your profile currency. Change per item if you paid in a different currency.
                      </p>
                    </>
                  ) : (
                    <div className="text-gray-900 text-sm">{powder.price != null ? formatPrice(powder.price, powder.priceCurrency ?? currency.code) : '—'}</div>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Currency</label>
                  {isEditing ? (
                    <CurrencyPicker
                      value={purchaseCurrency}
                      onChange={setPurchaseCurrency}
                      pinnedCurrencies={pinnedCurrencies}
                      onPinChange={setPinnedCurrencies}
                      sellerDefault={currency.code}
                    />
                  ) : (
                    <div className="text-gray-900 text-sm">
                      {powder.priceCurrency ? getCurrencyInfo(powder.priceCurrency).label : '—'}
                    </div>
                  )}
                </div>
              </div>

              {/* Purchase Date */}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Purchase Date</label>
                {isEditing ? (
                  <input
                    type="date"
                    value={purchaseDate}
                    onChange={(e) => setPurchaseDate(e.target.value)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 ${CategoryColors.powder.ring} focus:border-transparent`}
                  />
                ) : (
                  <div className="text-gray-900 text-sm">
                    {(() => {
                      const v: any = powder.purchaseDate;
                      if (!v) return '—';
                      try {
                        const d = v instanceof Date ? v : typeof v?.toDate === 'function' ? v.toDate() : new Date(v);
                        return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
                      } catch { return '—'; }
                    })()}
                  </div>
                )}
              </div>

              {/* Storage & Barcode Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Storage</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={storage_}
                      onChange={(e) => setStorage_(e.target.value)}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 ${CategoryColors.powder.ring} focus:border-transparent`}
                    />
                  ) : (
                    <div className="text-gray-900 text-sm">{powder.storage || '—'}</div>
                  )}
                </div>
                {isEditing && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Barcode/SKU</label>
                    <input
                      type="text"
                      value={barcode}
                      onChange={(e) => setBarcode(e.target.value)}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 ${CategoryColors.powder.ring} focus:border-transparent font-mono`}
                    />
                  </div>
                )}
              </div>

              {/* Release Info - Inline (not collapsible) — parity with polish */}
              <div className="bg-gradient-to-r from-cyan-50 to-sky-50 border border-cyan-200 rounded-lg p-4">
                <div className="text-xs font-medium text-cyan-700 uppercase tracking-wide mb-2">📅 Release Info</div>
                {isEditing ? (
                  <ReleaseInfoSection
                    value={releaseInfo}
                    onChange={setReleaseInfo}
                    datalistId="powder-detail-brand-collection-suggestions"
                  />
                ) : (
                  <ReleaseInfoDisplay item={powder as any} />
                )}
              </div>

              {/* USAGE TRACKER - Owner only */}
              {!isPublicView && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
                {!isEditingUsage ? (
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold text-green-700">{timesUsed}</span>
                        <span className="text-sm text-green-600">times used</span>
                        <button
                          type="button"
                          onClick={() => setIsEditingUsage(true)}
                          className="text-xs text-green-600 hover:text-green-800 underline ml-2"
                        >
                          edit
                        </button>
                      </div>
                      {lastUsedDate && (
                        <div className="text-xs text-green-600 mt-1">
                          Last: {(() => {
                            try {
                              const date = lastUsedDate instanceof Date ? lastUsedDate : new Date(lastUsedDate);
                              return isNaN(date.getTime()) ? 'Invalid date' : date.toLocaleDateString();
                            } catch {
                              return 'Invalid date';
                            }
                          })()}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleLogUsage}
                      className={`px-4 py-3 rounded-lg font-bold text-lg transition-all ${
                        usageSaved 
                          ? 'bg-green-600 text-white scale-95' 
                          : 'bg-green-500 text-white hover:bg-green-600 hover:scale-105 active:scale-95'
                      }`}
                    >
                      {usageSaved ? '✓' : '+1'}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-green-800">Edit Usage</span>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleSaveUsageEdit}
                          className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setIsEditingUsage(false);
                            setTimesUsed(powder.timesUsed || 0);
                            setLastUsedDate(powder.lastUsedDate);
                          }}
                          className="px-3 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium text-green-700 block mb-1">Times Used</label>
                        <input
                          type="number"
                          min="0"
                          value={timesUsed}
                          onChange={(e) => setTimesUsed(parseInt(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-green-700 block mb-1">Last Used Date</label>
                        <input
                          type="date"
                          value={(() => {
                            try {
                              if (!lastUsedDate) return '';
                              const date = lastUsedDate instanceof Date ? lastUsedDate : new Date(lastUsedDate);
                              if (isNaN(date.getTime())) return '';
                              return date.toISOString().split('T')[0];
                            } catch {
                              return '';
                            }
                          })()}
                          onChange={(e) => {
                            if (e.target.value) {
                              setLastUsedDate(new Date(e.target.value));
                            } else {
                              setLastUsedDate(undefined);
                            }
                          }}
                          className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              )}
            </div>
          </div>

          {/* COLLAPSIBLE SECTIONS - Full width below */}
          <div className="mt-6 space-y-2">

              {/* Tags - Collapsible */}
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection('tags')}
                  className="w-full flex items-center justify-between px-4 py-3 border border-gray-200 hover:bg-gray-50 transition-default"
                >
                  <div className="flex items-center gap-2">
                    <AppIcons.Tags className="w-5 h-5 text-current" aria-label="Custom tags" />
                    <span className="font-medium text-gray-600">Custom tags</span>
                    {tags.length > 0 && (
                      <span className={`text-xs ${CategoryColors.powder.light} ${CategoryColors.powder.text} px-2 py-0.5 rounded-full`}>
                        {tags.length}
                      </span>
                    )}
                  </div>
                  {expandedSections.has('tags') ? <ChevronDown className="icon-md" /> : <ChevronRight className="icon-md" />}
                </button>
                {expandedSections.has('tags') && (
                  <div className="p-4 space-y-3">
                    {tags.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {tags.map((tag, index) => (
                          <span
                            key={index}
                            className={`inline-flex items-center gap-1 px-3 py-1 ${CategoryColors.powder.light} ${CategoryColors.powder.text} rounded-full text-sm font-medium`}
                          >
                            {tag}
                            <button
                              type="button"
                              onClick={() => handleRemoveTag(tag)}
                              className={`ml-1 ${CategoryColors.powder.text} ${CategoryColors.powder.textHover} font-bold`}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyDown={handleTagKeyDown}
                        placeholder="Add a tag..."
                        className={`flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 ${CategoryColors.powder.ring} focus:border-transparent text-sm`}
                      />
                      <button
                        type="button"
                        onClick={handleAddTag}
                        disabled={!newTag.trim()}
                        className={`px-4 py-2 ${CategoryColors.powder.bgActive} text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium`}
                      >
                        Add
                      </button>
                    </div>
                    {tagsSaved && <span className="text-sm text-green-600">✓ Tag saved!</span>}
                    {tags.length === 0 && <p className="text-sm text-gray-500 italic">No tags yet.</p>}
                  </div>
                )}
              </div>

              {/* Notes - Collapsible */}
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection('notes')}
                  className="w-full flex items-center justify-between px-4 py-3 border border-gray-200 hover:bg-gray-50 transition-default"
                >
                  <span className="font-medium text-gray-600">📝 Notes</span>
                  {expandedSections.has('notes') ? <ChevronDown className="icon-md" /> : <ChevronRight className="icon-md" />}
                </button>
                {expandedSections.has('notes') && (
                  <div className="p-4 space-y-3">
                    {isPublicView ? (
                      publicNotes ? (
                        <div className="text-gray-600">{publicNotes}</div>
                      ) : (
                        <div className="text-gray-500 italic">No public notes</div>
                      )
                    ) : (
                      <>
                        <div>
                          <label className="text-sm font-medium text-gray-600 block mb-1">📝 Public Notes</label>
                          <textarea
                            value={publicNotes}
                            onChange={(e) => setPublicNotes(e.target.value)}
                            className={`w-full px-3 py-2 border border-gray-200 border border-gray-300 rounded-lg focus:ring-2 ${CategoryColors.powder.ring} resize-none`}
                            rows={3}
                            placeholder="Notes others can see..."
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600 block mb-1">🔒 Private Notes</label>
                          <textarea
                            value={privateNotes}
                            onChange={(e) => setPrivateNotes(e.target.value)}
                            className={`w-full px-3 py-2 bg-yellow-50 border border-yellow-300 rounded-lg focus:ring-2 ${CategoryColors.powder.ring} resize-none`}
                            rows={3}
                            placeholder="Notes only you can see..."
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleSaveNotes}
                          className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-default ${
                            notesSaved 
                              ? 'bg-green-600 text-white' 
                              : `${CategoryColors.powder.bgActive} text-white`
                          }`}
                        >
                          <Save className="icon-sm" />
                          {notesSaved ? 'Saved!' : 'Save Notes'}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Toggles Section - Owner only */}
              {!isPublicView && (
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection('toggles')}
                  className="w-full flex items-center justify-between px-4 py-3 border border-gray-200 hover:bg-gray-50 transition-default"
                >
                  <div className="flex items-center gap-2">
                    <AppIcons.Controls className="w-5 h-5 text-current" aria-label="Controls" />
                    <span className="font-medium text-gray-900">Toggles</span>
                    {getActiveToggleIcons().length > 0 && (
                      <span className="flex items-center gap-1">{getActiveToggleIcons()}</span>
                    )}
                    {toggleSaved && <span className="text-green-600 text-xs">✓ Saved</span>}
                  </div>
                  {expandedSections.has('toggles') ? <ChevronDown className="icon-md text-gray-500" /> : <ChevronRight className="icon-md text-gray-500" />}
                </button>
                {expandedSections.has('toggles') && (
                  <div className="p-4 bg-white grid grid-cols-2 md:grid-cols-4 gap-2">
                    <button type="button" onClick={() => handleToggle('isFavorite', !isFavorite)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-default ${isFavorite ? 'bg-pink-100 text-pink-800 border border-pink-300' : 'border-2 border-gray-300 text-gray-600 hover:bg-gray-200'}`}>
                      <Heart className={`w-4 h-4 ${isFavorite ? 'fill-pink-500' : ''}`} />
                      <span className="text-sm">Favorite</span>
                    </button>
                    <button type="button" onClick={() => handleToggle('onWishlist', !onWishlist)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-default ${onWishlist ? 'bg-amber-100 text-amber-800 border border-amber-300' : 'border-2 border-gray-300 text-gray-600 hover:bg-gray-200'}`}>
                      <Star className={`w-4 h-4 ${onWishlist ? 'fill-amber-500 text-amber-500' : ''}`} />
                      <span className="text-sm">PublicWishlist</span>
                    </button>
                    <button type="button" onClick={() => handleToggle('isRareLE', !isRareLE)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-default ${isRareLE ? 'bg-purple-100 text-purple-800 border border-purple-300' : 'border-2 border-gray-300 text-gray-600 hover:bg-gray-200'}`}>
                      <AppIcons.RareLE className={`w-4 h-4 ${isRareLE ? 'fill-purple-500' : ''}`} />
                      <span className="text-sm">Rare/Limited Edition</span>
                    </button>
                    <button type="button" onClick={() => handleToggle('needsInfo', !needsInfo)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-default ${needsInfo ? 'bg-blue-100 text-blue-800 border border-blue-300' : 'border-2 border-gray-300 text-gray-600 hover:bg-gray-200'}`}>
                      <Info className={`w-4 h-4 ${needsInfo ? 'fill-blue-500' : ''}`} />
                      <span className="text-sm">Needs Info</span>
                    </button>
                    <button type="button" onClick={() => handleToggle('swatched', !swatched)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-default ${swatched ? 'bg-teal-100 text-teal-800 border border-teal-300' : 'border-2 border-gray-300 text-gray-600 hover:bg-gray-200'}`}>
                      <Eye className={`w-4 h-4 ${swatched ? 'text-teal-600' : ''}`} />
                      <span className="text-sm">Swatched</span>
                    </button>
                    <button type="button" onClick={() => handleToggle('isPrivate', !isPrivate)}
                      className={`col-span-2 md:col-span-4 flex items-center gap-2 px-3 py-2 rounded-lg transition-default ${isPrivate ? 'bg-slate-200 text-slate-800 border border-slate-400' : 'border-2 border-gray-300 text-gray-600 hover:bg-gray-200'}`}>
                      <span className="text-base">🔒</span>
                      <div className="flex-1 text-left">
                        <span className="text-sm">Private (hide from public profile)</span>
                        {isPrivate && (powder.isForDestash || isForDestash) && (
                          <div className="mt-1 text-xs text-amber-600 italic">
                            This item is also listed for Destash — buyers can still see it on Marketplace.
                          </div>
                        )}
                      </div>
                    </button>
                  </div>
                )}

                {/* ISO Priority Selector - Only shows when on wishlist */}
                {expandedSections.has('toggles') && onWishlist && (
                  <div className="px-4 pb-4 bg-white border-t border-gray-100">
                    <div className="pt-3">
                      <p className="text-xs text-gray-500 mb-2 font-medium">How badly do you want this?</p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleIsoPriorityChange('iso')}
                          className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-default ${
                            isoPriority === 'iso'
                              ? PriorityColors.iso.badge
                              : 'border-2 border-gray-300 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {PriorityColors.iso.label}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleIsoPriorityChange('diso')}
                          className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-default ${
                            isoPriority === 'diso'
                              ? PriorityColors.diso.badge
                              : 'border-2 border-gray-300 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {PriorityColors.diso.label}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleIsoPriorityChange('unicorn')}
                          className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-default ${
                            isoPriority === 'unicorn'
                              ? PriorityColors.unicorn.badge
                              : 'border-2 border-gray-300 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {PriorityColors.unicorn.label}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              )}

              {/* MARKETPLACE LISTING SECTION - Owner only */}
              {!isPublicView && (
              <div className={`border rounded-lg overflow-hidden ${isForDestash ? 'border-green-300 bg-green-50' : 'border border-gray-300'}`}>
                <button
                  type="button"
                  onClick={() => toggleSection('marketplace')}
                  className={`w-full flex items-center justify-between px-4 py-3 transition-default ${isForDestash ? 'bg-green-100 hover:bg-green-200' : 'border border-gray-200 hover:bg-gray-50'}`}
                >
                  <div className="flex items-center gap-2">
                    <Store className={`w-5 h-5 ${isForDestash ? 'text-green-600' : 'text-gray-500'}`} />
                    <span className={`font-medium ${isForDestash ? 'text-green-800' : 'text-gray-900'}`}>
                      List for Destash
                    </span>
                    {isForDestash && (
                      <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full font-bold">LISTED</span>
                    )}
                    {destashSaved && <span className="text-green-600 text-xs ml-2">✓ Saved</span>}
                  </div>
                  {expandedSections.has('marketplace') ? <ChevronDown className="icon-md text-gray-500" /> : <ChevronRight className="icon-md text-gray-500" />}
                </button>
                {expandedSections.has('marketplace') && (
                  <div className="p-4 bg-white space-y-4">
                    {/* Main Toggle */}
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isForDestash}
                        onChange={(e) => handleToggleDestash(e.target.checked)}
                        className="w-5 h-5 rounded text-green-500 focus:ring-green-500"
                      />
                      <span className="font-medium text-gray-600">List on Marketplace</span>
                    </label>

                    {/* Expanded fields when listing is enabled */}
                    {isForDestash && (
                      <div className="space-y-4 pt-2 border-t border border-gray-300">
                        {/* Currently Listed Status (when already saved) */}
                        {wasAlreadyListed && powder.destashStatus === 'available' && !showListedSuccess && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-green-700">
                              <Store className="icon-sm" />
                              <span className="font-medium text-sm">Currently listed on Marketplace</span>
                            </div>
                            <a
                              href="/marketplace?tab=destash"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-green-600 hover:text-green-800 text-sm font-medium flex items-center gap-1"
                            >
                              View
                              <ExternalLink className="icon-xs" />
                            </a>
                          </div>
                        )}

                        {/* Quantity Available - Only show if quantity > 1 */}
                        {(parseInt(quantity) || 1) > 1 && (
                          <div>
                            <label className="block text-sm font-bold text-gray-600 mb-2 font-body">
                              How many to list? <span className="text-xs text-gray-500 font-normal">(You have {quantity})</span>
                            </label>
                            <div className="flex flex-wrap gap-2">
                              {Array.from({ length: parseInt(quantity) || 1 }, (_, i) => i + 1).map((num) => (
                                <button
                                  key={num}
                                  type="button"
                                  onClick={() => setDestashQuantityAvailable(num)}
                                  className={`w-10 h-10 rounded-lg font-bold transition-default ${
                                    destashQuantityAvailable === num
                                      ? 'bg-green-500 text-white'
                                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                  }`}
                                >
                                  {num}
                                </button>
                              ))}
                            </div>
                            {destashQuantityAvailable < (parseInt(quantity) || 1) && (
                              <p className="mt-2 text-sm text-gray-600">
                                ✨ Listing {destashQuantityAvailable}, keeping {(parseInt(quantity) || 1) - destashQuantityAvailable}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Condition - Required */}
                        <ConditionSelector
                          itemType="powder"
                          value={destashCondition}
                          onChange={setDestashCondition}
                          required={true}
                        />

                        {/* Price - Required */}
                        <div>
                          <label className="block text-sm font-bold text-gray-600 mb-1 font-body">
                            Price <span className="text-red-500">*</span>
                          </label>
                          <div className="relative w-36">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{currency.symbol}</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={destashPrice}
                              onChange={(e) => setDestashPrice(e.target.value)}
                              placeholder="0.00"
                              className={`w-full ${symbolPad} pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${!destashPrice ? 'border-amber-300' : 'border border-gray-300'}`}
                            />
                          </div>
                          {!destashPrice && (
                            <p className="mt-1 text-xs text-amber-600">Please enter a price</p>
                          )}
                          <p className="mt-1 text-xs text-gray-700 italic">
                            Shown to buyers in <strong>{currency.label}</strong>. Change your currency in <a href="/settings" className="underline hover:text-purple-600">Settings</a>.
                          </p>
                        </div>

                        {/* Open to Swaps - Optional */}
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isOpenToSwaps}
                            onChange={(e) => setIsOpenToSwaps(e.target.checked)}
                            className="w-5 h-5 rounded text-blue-500 focus:ring-blue-500"
                          />
                          <Repeat className="icon-sm text-blue-500" />
                          <span className="font-medium text-gray-600">Open to Swaps</span>
                          <span className="text-xs text-gray-500">(optional)</span>
                        </label>

                        {/* Notes - Optional */}
                        <div>
                          <label className="block text-sm font-bold text-gray-600 mb-1 font-body">
                            Destash Notes <span className="text-xs text-gray-500 font-normal">(optional)</span>
                          </label>
                          <textarea
                            value={destashNotes}
                            onChange={(e) => setDestashNotes(e.target.value)}
                            placeholder="e.g., Used once, almost full..."
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                          />
                        </div>

                        {/* Save Button */}
                        <button
                          type="button"
                          onClick={handleSaveDestashListing}
                          disabled={!destashCondition || !destashPrice}
                          className="w-full py-2 px-4 bg-green-500 text-white rounded-lg font-bold hover:bg-green-600 transition-default disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          <Store className="icon-sm" />
                          {wasAlreadyListed ? 'Update Listing' : 'Save & List on Marketplace'}
                      </button>

                      {/* Success Banner */}
                      {showListedSuccess && (
                        <div className="bg-green-100 border border-green-300 rounded-lg p-4">
                          <div className="flex items-center gap-2 text-green-800 font-bold mb-2">
                            <span className="text-xl">🎉</span>
                            <span>{wasAlreadyListed ? 'Listing Updated!' : 'Listed on Marketplace!'}</span>
                          </div>
                          <p className="text-sm text-green-700 mb-3">
                            Your item is now visible to other collectors.
                          </p>
                          <a
                            href="/marketplace?tab=destash"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-default text-sm"
                          >
                            <ExternalLink className="icon-sm" />
                            View in Marketplace
                          </a>
                        </div>
                      )}

                      {/* Warning message */}
                      {!showListedSuccess && (
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <AppIcons.Warning className="w-4 h-4 text-current" aria-label="Warning" />
                          This item will be visible to other users on the marketplace
                        </p>
                      )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}

          </div>

          {/* ARCHIVE & DELETE SECTION - Owner only */}
          {!isPublicView && (
          <div className="mt-6 border-t border border-gray-300 pt-6">
            {isInTrash ? (
              /* Item is in trash - show restore and permanent delete */
              <div className="border-2 border-red-200 bg-red-50 rounded-lg p-4">
                <div className="flex items-center gap-2 text-red-700 mb-3">
                  <Trash2 className="w-5 h-5" />
                  <span className="font-medium">This item is in the Trash</span>
                </div>
                <p className="text-sm text-red-600 mb-4">
                  Restore this item to return it to your collection, or delete it permanently.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    onClick={handleRestore}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-default font-medium"
                  >
                    <RefreshCw className="w-5 h-5" />
                    <span>Restore to Collection</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPermanentDeleteConfirm(true)}
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-default font-medium"
                  >
                    <AppIcons.Delete className="w-5 h-5 text-current" aria-label="Delete" />
                    <span>Delete Forever</span>
                  </button>
                </div>
              </div>
            ) : powder.isDestashed ? (
              <div className="border-2 border-gray-300 border border-gray-300 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <AppIcons.Archive className="w-5 h-5 text-current" aria-label="Archive" />
                      <span className="font-medium">Archived</span>
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {powder.destashMethod && <span className="capitalize">{powder.destashMethod}</span>}
                      {powder.destashSalePrice && <span> for ${powder.destashSalePrice.toFixed(2)}</span>}
                      {powder.destashDate && (
                        <span> on {new Date(powder.destashDate).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleUnArchive}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-default text-sm font-medium"
                  >
                    🔄 I Got It Back!
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => setShowArchiveModal(true)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-200 transition-default font-medium"
                >
                  <AppIcons.Archive className="w-5 h-5 text-current" aria-label="Archive" />
                  <span>Mark as Gone (Sold, Traded, etc.)</span>
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-default font-medium border border-red-200"
                >
                  <Trash2 className="w-5 h-5" />
                  <span>Move to Trash</span>
                </button>
              </div>
            )}
          </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 border border-gray-200 px-4 md:px-6 py-3 md:py-4 border-t border border-gray-300">
          <button
            type="button"
            onClick={handleClose}
            className={`w-full px-6 py-3 ${CategoryColors.powder.bgActive} text-white rounded-lg font-semibold transition-default`}
          >
            Close
          </button>
        </div>
      </div>

      {/* ARCHIVE MODAL - For when item actually leaves collection */}
      {showArchiveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="glass-modal max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <AppIcons.Archive className="w-6 h-6 text-current" aria-label="Archive" />
              Archive This Powder
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Use this when the item has left your collection (sold, traded, used up, etc.)
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-1">How did it leave?</label>
                <select
                  value={archiveMethod}
                  onChange={(e) => setArchiveMethod(e.target.value)}
                  className={`w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 ${CategoryColors.powder.ring} focus:border-transparent`}
                >
                  <option value="sold">💵 Sold</option>
                  <option value="traded">🔄 Traded</option>
                  <option value="gifted">🎁 Gifted</option>
                  <option value="lost">❓ Lost</option>
                  <option value="damaged">💔 Damaged</option>
                  <option value="used-up">✨ Used Up</option>
                  <option value="other">📝 Other</option>
                </select>
              </div>

              {archiveMethod === 'sold' && (
                <div>
                  <label className="text-sm font-medium text-gray-600 block mb-1">Sale Price (optional)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{currency.symbol}</span>
                    <input
                      type="number"
                      step="0.01"
                      value={archiveSalePrice}
                      onChange={(e) => setArchiveSalePrice(e.target.value)}
                      placeholder="0.00"
                      className={`w-full ${symbolPad} pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 ${CategoryColors.powder.ring} focus:border-transparent`}
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-gray-600 block mb-1">Notes (optional)</label>
                <textarea
                  value={archiveNotes}
                  onChange={(e) => setArchiveNotes(e.target.value)}
                  placeholder="e.g., Gave to a friend"
                  rows={2}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 ${CategoryColors.powder.ring} focus:border-transparent resize-none`}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowArchiveModal(false)}
                className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-200 transition-default font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleArchive}
                className={`flex-1 px-4 py-2 ${CategoryColors.powder.bgActive} text-white rounded-lg transition-default font-medium`}
              >
                ✓ Archive Item
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL (Soft delete - moves to trash) */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="glass-modal max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-red-600 mb-2 flex items-center gap-2">
              <Trash2 className="w-6 h-6" />
              Move to Trash?
            </h3>
            
            <p className="text-white mb-4">
              This will move the following item to your Trash:
            </p>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="font-bold text-gray-900">{powder.name}</div>
              <div className="text-gray-600">by {powder.brand}</div>
            </div>
            
            <p className="text-amber-600 font-medium text-sm mb-6">
              ⚠️ You can restore it later from the Trash filter.
            </p>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 border-2 border-slate-500 text-white rounded-lg hover:bg-slate-600 transition-default font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-default font-medium"
              >
                <Trash2 className="w-4 h-4" />
                Move to Trash
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PERMANENT DELETE CONFIRMATION MODAL */}
      {showPermanentDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="glass-modal max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-red-600 mb-2 flex items-center gap-2">
              <AppIcons.Warning className="w-6 h-6 text-current" aria-label="Warning" />
              Delete Forever?
            </h3>
            
            <p className="text-white mb-4">
              This will permanently remove:
            </p>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="font-bold text-gray-900">{powder.name}</div>
              <div className="text-gray-600">by {powder.brand}</div>
            </div>
            
            <p className="text-red-600 font-medium text-sm mb-6">
              ⚠️ This cannot be undone! All data including photos will be permanently deleted.
            </p>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowPermanentDeleteConfirm(false)}
                className="flex-1 px-4 py-2 border-2 border-slate-500 text-white rounded-lg hover:bg-slate-600 transition-default font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePermanentDelete}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-default font-medium"
              >
                <AppIcons.Delete className="w-4 h-4 text-current" aria-label="Delete" />
                Yes, Delete Forever
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Storage Upgrade Modal */}
      {showStorageUpgradeModal && (
        <StorageUpgradeModal
          isOpen={showStorageUpgradeModal}
          onClose={() => setShowStorageUpgradeModal(false)}
        />
      )}
    </div>
  );
}
